/**
 * AtlasProbesModule — the PROBES window (2026-09-18).
 *
 * "Instead of having the perf tour in the debug window add a probe button and
 * have a separate window that pops up for different probes to be ran by the
 * admin to test and record metrics from features, add the perf tour on that
 * window instead of the debug window."
 *
 * ONE window (`makeWindow` id `probes` — the SWG chrome: drag, resize, roll-up,
 * remembered geometry) with:
 *   · the PERF TOUR card at the top — `AtlasPerfTourModule.buildSection` renders
 *     its tier boxes / seconds / Run / Abort / status here now (the debug panel
 *     no longer carries it);
 *   · a CATALOG of probes, one Run button each, grouped (performance, render,
 *     assets, world, systems). A probe is a pure READ of a diagnostic lever the
 *     modules already register on `window` (`__pipelines`, `__triangleCensus`,
 *     `__drawCensus`, `__foliageCensus`, `__settlements`, `__water`, `__fauna`,
 *     `__bounty` …) — the levers every browser harness reads — or a TIMED
 *     sampler (frame timing over N seconds: fps, frame-ms percentiles, draw
 *     calls, triangles, Long Animation Frames, heap);
 *   · the RESULT box (the last probe's JSON, Copy) and the SESSION LOG (every
 *     run of this page load with its time, cost and outcome; Export JSON
 *     downloads the whole log; Clear).
 *
 * Nothing here writes to the server: the perf tour records its own rows
 * through its debug-gated reducers; the probes are diagnostics for the admin's
 * eyes and clipboard. The window opens from the debug panel's "Probes…" button
 * (backtick menu) or `__probes.open()`; the dev gate is the debug menu's (the
 * button lives inside it) plus the Commons admin capability for the lever.
 *
 * Levers: `__probes.open() / close() / isOpen() / list() / run(id, { seconds })
 * / records() / clear() / exportJson()`.
 */
import type { App, IModule } from 'enginex/core/App.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { capsIn } from '../lib/world_caps.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { activeTier, activePreset } from '../lib/quality_tiers.js';
import type { AtlasPerfTourModule } from './AtlasPerfTourModule.js';
import type { AtlasShaderTourModule } from './AtlasShaderTourModule.js';

export type ProbeGroup = 'performance' | 'render' | 'assets' | 'audio' | 'world' | 'systems';
export interface ProbeDef {
  id: string;
  title: string;
  desc: string;
  group: ProbeGroup;
  /** A timed probe samples for `seconds` (the window's input). */
  timed?: boolean;
  run: (opts: { seconds: number }) => Promise<unknown> | unknown;
}
export interface ProbeRecord {
  id: string;
  at: number;          // epoch ms
  ms: number;          // the probe's cost
  ok: boolean;
  result: unknown;
  error?: string;
  seconds?: number;
  tier: string;
  x: number;
  z: number;
}

const GROUP_LABEL: Record<ProbeGroup, string> = { performance: 'Performance', render: 'Render', assets: 'Assets & memory', audio: 'Audio', world: 'World', systems: 'Systems' };
const g = globalThis as unknown as Record<string, unknown>;
const pct = (sorted: number[], q: number): number => (sorted.length ? sorted[Math.min(sorted.length - 1, Math.max(0, Math.floor(q * (sorted.length - 1))))] : 0);

/** Call a `window.__x` lever: a function is invoked (with `args`), an object is snapshotted (its functions dropped). */
function lever(name: string, ...args: unknown[]): unknown {
  const v = g[name];
  if (v === undefined || v === null) return { missing: name, hint: 'the owning module is not loaded (not in the Commons, or the feature is off)' };
  if (typeof v === 'function') return (v as (...a: unknown[]) => unknown)(...args);
  if (typeof v === 'object') { const out: Record<string, unknown> = {}; for (const [k, x] of Object.entries(v as Record<string, unknown>)) if (typeof x !== 'function') out[k] = x; return out; }
  return v;
}

/** JSON-safe copy: bigints → strings, functions dropped, depth / length / string caps so a census never floods the box. */
export function safeJson(v: unknown, depth = 0): unknown {
  if (v === null || v === undefined) return v;
  if (typeof v === 'bigint') return v.toString();
  if (typeof v === 'number') return Number.isFinite(v) ? (Math.abs(v) < 1e6 ? Math.round(v * 1000) / 1000 : Math.round(v)) : String(v);
  if (typeof v === 'string') return v.length > 2000 ? `${v.slice(0, 2000)}… (+${v.length - 2000})` : v;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'function' || typeof v === 'symbol') return undefined;
  if (depth > 7) return '…';
  if (Array.isArray(v)) return (v.length > 300 ? [...v.slice(0, 300), `… (+${v.length - 300})`] : v).map((x) => safeJson(x, depth + 1));
  if (v instanceof Map) return safeJson([...v.entries()], depth);
  if (v instanceof Set) return safeJson([...v], depth);
  if (typeof v === 'object') {
    const out: Record<string, unknown> = {};
    let n = 0;
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) { if (++n > 200) { out['…'] = 'more keys'; break; } const y = safeJson(x, depth + 1); if (y !== undefined) out[k] = y; }
    return out;
  }
  return String(v);
}

export class AtlasProbesModule implements IModule {
  readonly name = 'Probes';
  private app: App | null = null;
  private win: UiWindow | null = null;
  private resultHead: HTMLDivElement | null = null;
  private resultPre: HTMLPreElement | null = null;
  private logBody: HTMLTableSectionElement | null = null;
  private secondsInput: HTMLInputElement | null = null;
  private busy: HTMLDivElement | null = null;
  private records: ProbeRecord[] = [];
  private running: string | null = null;
  private catalog: ProbeDef[] = [];
  // the timed frame sampler
  private sampling = false;
  private lastFrameAt = 0;
  private frameTimes: number[] = [];
  private drawCallsSeen: number[] = [];
  private trianglesSeen: number[] = [];

  init(app: App): void {
    this.app = app;
    this.catalog = this.buildCatalog();
    app.world.addSystem(makeSystem('AtlasProbesClock', Phase.UI, 91, () => this.frame()));
    (window as unknown as Record<string, unknown>).__probes = {
      open: () => this.open(),
      close: () => this.win?.setOpen(false),
      isOpen: () => !!this.win?.isOpen(),
      list: () => this.catalog.map((p) => ({ id: p.id, title: p.title, group: p.group, timed: !!p.timed })),
      run: (id: string, opts?: { seconds?: number }) => this.run(id, opts?.seconds),
      records: () => this.records.map((r) => safeJson(r)),
      clear: () => { this.records = []; this.renderLog(); },
      exportJson: () => this.exportJson(),
      running: () => this.running,
    };
  }

  dispose(): void {
    this.win?.root.remove();
    this.win = null;
    delete (window as unknown as Record<string, unknown>).__probes;
  }

  /** Commons admin only (the same gate as the Versona Universe) — the button lives inside the dev-gated debug panel anyway. */
  open(): boolean {
    if (atlas.currentWorldId === null || atlas.currentWorldId !== atlas.commonsWorldId || !capsIn(atlas.currentWorldId).admin) return false;
    if (!this.win) this.build();
    this.win!.setOpen(true);
    return true;
  }

  // ── the frame clock (only while a timed probe samples) ──
  private frame(): void {
    const now = performance.now();
    if (this.sampling && this.lastFrameAt > 0) {
      this.frameTimes.push(now - this.lastFrameAt);
      const native = this.app?.renderer.native as unknown as { info?: { render?: { drawCalls?: number; triangles?: number } } } | undefined;
      this.drawCallsSeen.push(native?.info?.render?.drawCalls ?? 0);
      this.trianglesSeen.push(native?.info?.render?.triangles ?? 0);
    }
    this.lastFrameAt = now;
  }

  /** Frame timing over `seconds` of wall clock: fps, frame-ms percentiles, per-frame draw calls / triangles, LoAFs, heap. */
  private async sampleFrames(seconds: number): Promise<unknown> {
    this.frameTimes = []; this.drawCallsSeen = []; this.trianglesSeen = [];
    this.lastFrameAt = 0;
    const start = performance.now();
    this.sampling = true;
    try { await new Promise((r) => setTimeout(r, Math.max(1, seconds) * 1000)); } finally { this.sampling = false; }
    const elapsedS = Math.max(0.25, (performance.now() - start) / 1000);
    const sorted = [...this.frameTimes].sort((a, b) => a - b);
    const loafs = ((lever('__loaf', 600) as Array<{ t: number; dur: number; scripts?: unknown }> | undefined) ?? []);
    const inWindow = Array.isArray(loafs) ? loafs.filter((l) => l.t >= start) : [];
    const heap = (performance as unknown as { memory?: { usedJSHeapSize?: number; totalJSHeapSize?: number } }).memory;
    const mean = (a: number[]): number => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0);
    return {
      seconds: elapsedS, frames: sorted.length, fps: sorted.length / elapsedS,
      frameMs: { p50: pct(sorted, 0.5), p95: pct(sorted, 0.95), p99: pct(sorted, 0.99), max: sorted.length ? sorted[sorted.length - 1] : 0, mean: mean(sorted) },
      drawCalls: { mean: mean(this.drawCallsSeen), max: Math.max(0, ...this.drawCallsSeen) },
      triangles: { mean: mean(this.trianglesSeen), max: Math.max(0, ...this.trianglesSeen) },
      loafs: { count: inWindow.length, totalMs: inWindow.reduce((s, l) => s + (l.dur ?? 0), 0), worst: inWindow.slice().sort((a, b) => (b.dur ?? 0) - (a.dur ?? 0)).slice(0, 5) },
      heapMb: heap?.usedJSHeapSize ? heap.usedJSHeapSize / 1048576 : null,
      pipelines: lever('__pipelines'),
    };
  }

  private buildCatalog(): ProbeDef[] {
    const L = (id: string, title: string, desc: string, group: ProbeGroup, name: string, ...args: unknown[]): ProbeDef => ({ id, title, desc, group, run: () => lever(name, ...args) });
    return [
      { id: 'frame-timing', title: 'Frame timing', desc: 'fps, frame-ms p50 / p95 / p99 / max, per-frame draw calls and triangles, Long Animation Frames and heap over N seconds', group: 'performance', timed: true, run: ({ seconds }) => this.sampleFrames(seconds) },
      L('pipelines', 'Pipelines', 'three\'s program / pipeline count, calls and triangles (the variant count the CPU profile blamed)', 'performance', '__pipelines'),
      L('loaf', 'Long Animation Frames', 'the last 600 LoAFs ≥ 50 ms with script attribution', 'performance', '__loaf', 600),
      L('triangle-census', 'Triangle census', 'a frame\'s triangles by object family (top 12) — where the triangles come from', 'render', '__triangleCensus', 12),
      L('draw-census', 'Draw census', 'draw calls by material family with triangles PER INSTANCE (an oversized asset vs an oversized scene)', 'render', '__drawCensus'),
      L('foliage-census', 'Foliage census', 'blades, impostors, forest trees, colliders and the scatter queue', 'render', '__foliageCensus'),
      { id: 'graphics-state', title: 'Graphics state', desc: 'the active tier and preset, the post passes, the drawing buffer, the clouds gate', group: 'render', run: () => { const gfx = g.__gfx as Record<string, (() => unknown) | undefined> | undefined; return { tier: activeTier(), preset: activePreset(), passes: gfx?.passes?.(), drawingBuffer: gfx?.drawingBuffer?.(), clouds: gfx?.clouds?.(), adaptive: lever('__adaptive') }; } },
      L('impostors', 'Impostors', 'the octahedral impostor bakes and their instances', 'render', '__impostors'),
      L('sky', 'Sky', 'the sky module: source, night factor, time of day', 'render', '__sky'),
      L('night-stars', 'Night stars', 'the star catalogue on the GPU: count, entries, cells, bytes', 'render', '__nightStars'),
      { id: 'js-heap', title: 'JS heap', desc: 'performance.memory (Chromium)', group: 'assets', run: () => { const m = (performance as unknown as { memory?: { usedJSHeapSize?: number; totalJSHeapSize?: number; jsHeapSizeLimit?: number } }).memory; return m ? { usedMb: (m.usedJSHeapSize ?? 0) / 1048576, totalMb: (m.totalJSHeapSize ?? 0) / 1048576, limitMb: (m.jsHeapSizeLimit ?? 0) / 1048576 } : { unsupported: 'no performance.memory on this browser' }; } },
      L('gpu-shield', 'GPU destroy shield', 'the WebGPU destroy shield\'s counters (risky buffers, reclaims)', 'assets', '__shieldStats'),
      L('asset-cache', 'Asset cache', 'the OPFS / memory asset cache: hits, misses, bytes', 'assets', '__assetCache'),
      L('asset-bytes', 'Asset bytes LRU', 'the 96 MB GLB bytes cache', 'assets', '__assetBytes'),
      L('asset-packs', 'Asset packs', 'the .vpak packs opened and their members', 'assets', '__assetPacks'),
      L('texture-share', 'Texture share', 'KTX2 textures shared across LOD files', 'assets', '__textureShare'),
      L('warm-stats', 'Pipeline warm', 'warmThenShow / residency statistics', 'assets', '__warmStats'),
      L('near-lod', 'Near LOD policy', 'the tier\'s near foliage / prop LOD levels', 'assets', '__nearLod'),
      L('settlements', 'Settlements', 'built / assembling sites, the refine ladder, the LOD0 ledger, colliders', 'world', '__settlements'),
      L('proxies', 'Settlement proxies', 'the HLOD-0 / HLOD-1 far tier and its seat audit', 'world', '__proxies'),
      L('placements', 'Admin placements', 'the world-wide placements, their LOD and the cull', 'world', '__placements'),
      L('terrain', 'Terrain', 'the chunk window, readiness and the kernel', 'world', '__terrain'),
      L('far-terrain', 'Far terrain', 'the four clipmap levels, the far plane, the fog factor', 'world', '__farTerrain'),
      L('rivers', 'Rivers', 'ribbons, falls, the material and the look', 'world', '__rivers'),
      L('caves', 'Caves', 'cave sites, meshes and the grounding law', 'world', '__caves'),
      // ── audio (the shared runtime, 2026-09-20) ──
      L('audio-runtime', 'Audio runtime', 'the ONE context: state + latency, live voices by bus against the budget, steals and drops, loops (live / virtual), the mix (volumes, ducks, guard makeup), the listener pose, the room, decoded assets and failures, obstruction rays', 'audio', '__audio'),
      // The signal check renders the REAL graph offline and measures the samples — in THIS browser, which is the point:
      // it is the only honest way to learn that a Firefox (no AudioListener.forwardX) hears the right ear as the right ear.
      { id: 'audio-signal', title: 'Audio signal check', desc: 'renders the real mix graph through an OfflineAudioContext IN THIS BROWSER and judges the samples: the game gate is silence, a category at 0 % does not leak through the reverb, under water loses the hiss and keeps the alarm, a source to your right is in your right ear (and the left when you turn), HRTF agrees, mono folds, the guard is unity. ~1 s, no sound is played', group: 'audio',
        run: async () => { const { runRenderProbe } = await import('../lib/audio/render_probe.js'); const r = await runRenderProbe(); return { verdict: r.failed === 0 ? `PASS — ${r.passed} checks` : `FAIL — ${r.failed} of ${r.passed + r.failed}`, guardMakeupDb: r.guardMakeupDb, ms: r.ms, failed: r.checks.filter((c) => !c.pass), checks: r.checks.map((c) => `${c.pass ? '✓' : '✗'} ${c.name} — ${c.detail}`), userAgent: r.userAgent }; } },
      L('audio-zones', 'Audio zones', 'placed zones: role (bed region / point), inside?, the bed weights by priority, the loop each one holds, the asset URL it resolved', 'audio', '__audioZoneDiag'),
      L('music', 'Music', 'the score: phase, the track, why combat (creature / PvP / hunter / battle lease), the silence doctrine\'s clocks', 'audio', '__music'),
      L('ambience', 'Ambience', 'the bed, the murmur and the footsteps: surface, why steps are blocked (riding / flying / swimming…), buffer states', 'audio', '__ambience'),
      L('tts', 'Versona voices', 'the TTS worker: load, per-chunk synth ms, the live utterance and its priority', 'audio', '__tts'),
      L('water', 'Water', 'the sea and lakes: shore bake, reflection, the fluid field', 'world', '__water'),
      L('weather', 'Weather', 'kind, density, particles, the resolved field, audio, thunder', 'world', '__weather'),
      L('fauna', 'Fauna', 'birds by state and species, perches, models, the swarm', 'world', '__fauna'),
      L('footprints', 'Footprints', 'the decal pools and surfaces', 'world', '__footprints'),
      L('versona-life', 'Versona life', 'the crowd: slots, gaze, routes, the rig layer', 'systems', '__versonaLife'),
      L('chatter', 'Versona chatter', 'the eavesdrop threads', 'systems', '__chatter'),
      L('merchants', 'Merchants', 'the counters, queues and holds', 'systems', '__merchants'),
      L('combat', 'Combat', 'the last strike, projectiles, feel counters', 'systems', '__combat'),
      L('bounty', 'Bounty hunt', 'the stalking projection as the client sees it', 'systems', '__bounty'),
      L('outposts', 'War outposts', 'the field bases and their parts', 'systems', '__outposts'),
      L('space', 'Space', 'the sector: patrols, war, dens, location', 'systems', '__space'),
    ];
  }

  async run(id: string, seconds?: number): Promise<ProbeRecord> {
    const def = this.catalog.find((p) => p.id === id);
    if (!def) throw new Error(`unknown probe "${id}"`);
    if (this.running) throw new Error(`probe "${this.running}" is still running`);
    this.running = id;
    const secs = Math.max(1, Math.min(600, seconds ?? (Number(this.secondsInput?.value) || 10)));
    const t0 = performance.now();
    if (this.busy) this.busy.textContent = def.timed ? `${def.title} — sampling ${secs} s…` : `${def.title}…`;
    let rec: ProbeRecord;
    try {
      const result = safeJson(await def.run({ seconds: secs }));
      rec = { id, at: Date.now(), ms: performance.now() - t0, ok: true, result, seconds: def.timed ? secs : undefined, tier: activeTier(), x: atlas.localX, z: atlas.localZ };
    } catch (e) {
      rec = { id, at: Date.now(), ms: performance.now() - t0, ok: false, result: null, error: String((e as Error)?.message ?? e), seconds: def.timed ? secs : undefined, tier: activeTier(), x: atlas.localX, z: atlas.localZ };
    } finally {
      this.running = null;
      if (this.busy) this.busy.textContent = '';
    }
    this.records.push(rec);
    if (this.records.length > 200) this.records.splice(0, this.records.length - 200);
    this.showResult(rec);
    this.renderLog();
    return rec;
  }

  /** Another module's run (the shader tour) lands in the log and the result box. */
  addRecord(id: string, result: unknown, ok = true, ms = 0): void {
    const rec: ProbeRecord = { id, at: Date.now(), ms, ok, result: safeJson(result), error: ok ? undefined : String((result as { error?: string })?.error ?? 'failed'), tier: activeTier(), x: atlas.localX, z: atlas.localZ };
    this.records.push(rec);
    if (this.records.length > 200) this.records.splice(0, this.records.length - 200);
    this.showResult(rec);
    this.renderLog();
  }

  private exportJson(): string {
    return JSON.stringify({ exportedAt: new Date().toISOString(), userAgent: navigator.userAgent, tier: activeTier(), records: this.records }, null, 2);
  }

  // ── the window ──
  private build(): void {
    this.win = makeWindow({ id: 'probes', title: 'Probes (admin)', icon: 'gear', width: 860, height: 660, minWidth: 560, minHeight: 380, x: 60, y: 50 });
    const shell = document.createElement('div');
    shell.id = 'probes-window';
    shell.style.cssText = 'display:flex;height:100%;min-height:0;color:var(--ax-text);font:12px/1.45 var(--ax-font);box-sizing:border-box';
    // left: the perf tour card + the catalog
    const left = document.createElement('div');
    left.style.cssText = 'width:330px;min-width:260px;display:flex;flex-direction:column;border-right:1px solid var(--ax-line);min-height:0';
    const tourCard = document.createElement('div');
    tourCard.id = 'probes-perf-tour';
    tourCard.style.cssText = 'padding:8px 10px;border-bottom:1px solid var(--ax-line);background:var(--ax-bg2)';
    const th = document.createElement('div');
    th.textContent = 'Perf tour';
    th.style.cssText = 'font-weight:600;color:var(--ax-hi);margin-bottom:4px';
    tourCard.appendChild(th);
    const tour = this.app?.getModule<AtlasPerfTourModule>('PerfTour');
    if (tour) tour.buildSection(tourCard);
    else { const n = document.createElement('div'); n.style.cssText = 'font-size:11px;color:var(--ax-muted)'; n.textContent = 'PerfTour module not loaded'; tourCard.appendChild(n); }
    left.appendChild(tourCard);
    // THE SHADER TOUR card (2026-09-18): every shader alone, all at once, the interference combinations — pictures per leg.
    const shaderCard = document.createElement('div');
    shaderCard.id = 'probes-shader-tour';
    shaderCard.style.cssText = 'padding:8px 10px;border-bottom:1px solid var(--ax-line);background:var(--ax-bg2)';
    const sh = document.createElement('div');
    sh.textContent = 'Shader tour';
    sh.style.cssText = 'font-weight:600;color:var(--ax-hi);margin-bottom:4px';
    shaderCard.appendChild(sh);
    const shaderTour = this.app?.getModule<AtlasShaderTourModule>('ShaderTour');
    if (shaderTour) shaderTour.buildSection(shaderCard);
    else { const n = document.createElement('div'); n.style.cssText = 'font-size:11px;color:var(--ax-muted)'; n.textContent = 'ShaderTour module not loaded'; shaderCard.appendChild(n); }
    left.appendChild(shaderCard);
    const ctl = document.createElement('div');
    ctl.style.cssText = 'display:flex;gap:6px;align-items:center;padding:6px 10px;border-bottom:1px solid var(--ax-line);font-size:11px;color:var(--ax-soft)';
    this.secondsInput = document.createElement('input');
    this.secondsInput.type = 'number'; this.secondsInput.value = '10'; this.secondsInput.min = '1'; this.secondsInput.max = '600'; this.secondsInput.id = 'probes-seconds';
    this.secondsInput.style.cssText = 'width:56px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
    const runQuick = document.createElement('button');
    runQuick.id = 'probes-run-all';
    runQuick.textContent = 'Run every quick probe';
    runQuick.style.cssText = 'background:var(--ax-bg3);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:2px 8px;cursor:pointer;font:11px var(--ax-font)';
    runQuick.onclick = (): void => { void (async () => { for (const p of this.catalog) if (!p.timed) { try { await this.run(p.id); } catch { /* logged in the record */ } } })(); };
    ctl.append('Seconds (timed)', this.secondsInput, runQuick);
    left.appendChild(ctl);
    const list = document.createElement('div');
    list.id = 'probes-list';
    list.style.cssText = 'flex:1;overflow:auto;padding:4px 0';
    for (const grp of Object.keys(GROUP_LABEL) as ProbeGroup[]) {
      const gh = document.createElement('div');
      gh.textContent = GROUP_LABEL[grp];
      gh.style.cssText = 'font-weight:600;color:var(--ax-hi);padding:6px 10px 2px;border-top:1px solid var(--ax-line)';
      list.appendChild(gh);
      for (const p of this.catalog.filter((x) => x.group === grp)) {
        const row = document.createElement('div');
        row.dataset.probeRow = p.id;
        row.title = p.desc;
        row.style.cssText = 'display:flex;gap:6px;align-items:center;padding:2px 10px';
        const b = document.createElement('button');
        b.dataset.probe = p.id;
        b.textContent = 'Run';
        b.style.cssText = 'background:var(--ax-bg3);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 8px;cursor:pointer;font:11px var(--ax-font)';
        b.onclick = (): void => { void this.run(p.id).catch(() => { /* recorded */ }); };
        const t = document.createElement('span');
        t.textContent = p.title + (p.timed ? ' ⏱' : '');
        t.style.cssText = 'flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis';
        row.append(b, t);
        list.appendChild(row);
      }
    }
    left.appendChild(list);
    // right: the result + the log
    const right = document.createElement('div');
    right.style.cssText = 'flex:1;display:flex;flex-direction:column;min-width:0;min-height:0';
    const head = document.createElement('div');
    head.style.cssText = 'display:flex;gap:8px;align-items:center;padding:6px 10px;border-bottom:1px solid var(--ax-line);font-size:11px;color:var(--ax-soft)';
    this.resultHead = document.createElement('div');
    this.resultHead.id = 'probes-result-head';
    this.resultHead.style.cssText = 'flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis';
    this.resultHead.textContent = 'no probe run yet — pick one on the left';
    this.busy = document.createElement('div');
    this.busy.id = 'probes-busy';
    this.busy.style.cssText = 'color:var(--ax-hi)';
    const copy = document.createElement('button');
    copy.id = 'probes-copy';
    copy.textContent = 'Copy JSON';
    copy.style.cssText = 'background:var(--ax-bg3);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 8px;cursor:pointer;font:11px var(--ax-font)';
    copy.onclick = (): void => { const t = this.resultPre?.textContent ?? ''; void navigator.clipboard?.writeText(t).catch(() => { /* no clipboard */ }); };
    head.append(this.resultHead, this.busy, copy);
    this.resultPre = document.createElement('pre');
    this.resultPre.id = 'probes-result';
    this.resultPre.style.cssText = 'flex:1;margin:0;overflow:auto;padding:8px 10px;font:11px/1.4 var(--ax-mono);color:var(--ax-text);background:var(--ax-bg);min-height:120px;white-space:pre-wrap;word-break:break-word';
    const logHead = document.createElement('div');
    logHead.style.cssText = 'display:flex;gap:8px;align-items:center;padding:4px 10px;border-top:1px solid var(--ax-line);border-bottom:1px solid var(--ax-line);font-size:11px;color:var(--ax-soft);background:var(--ax-bg2)';
    const lt = document.createElement('span'); lt.textContent = 'Session log'; lt.style.cssText = 'flex:1;font-weight:600;color:var(--ax-hi)';
    const exp = document.createElement('button'); exp.id = 'probes-export'; exp.textContent = 'Export JSON';
    const clr = document.createElement('button'); clr.id = 'probes-clear'; clr.textContent = 'Clear';
    for (const b of [exp, clr]) b.style.cssText = 'background:var(--ax-bg3);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 8px;cursor:pointer;font:11px var(--ax-font)';
    exp.onclick = (): void => {
      const blob = new Blob([this.exportJson()], { type: 'application/json' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `probes-${new Date().toISOString().replace(/[:.]/g, '-')}.json`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    };
    clr.onclick = (): void => { this.records = []; this.renderLog(); };
    logHead.append(lt, exp, clr);
    const logWrap = document.createElement('div');
    logWrap.style.cssText = 'height:150px;overflow:auto;font-size:11px';
    const table = document.createElement('table');
    table.style.cssText = 'width:100%;border-collapse:collapse';
    const thead = document.createElement('thead');
    thead.innerHTML = '<tr style="color:var(--ax-soft);text-align:left"><th style="padding:2px 10px">time</th><th style="padding:2px 6px">probe</th><th style="padding:2px 6px">tier</th><th style="padding:2px 6px;text-align:right">cost</th><th style="padding:2px 6px">outcome</th></tr>';
    this.logBody = document.createElement('tbody');
    this.logBody.id = 'probes-log';
    table.append(thead, this.logBody);
    logWrap.appendChild(table);
    right.append(head, this.resultPre, logHead, logWrap);
    shell.append(left, right);
    this.win.body.appendChild(shell);
    this.renderLog();
  }

  private showResult(rec: ProbeRecord): void {
    if (this.resultHead) this.resultHead.textContent = `${rec.id} · ${new Date(rec.at).toLocaleTimeString()} · ${rec.ms.toFixed(0)} ms · ${rec.tier} · (${rec.x.toFixed(0)}, ${rec.z.toFixed(0)})${rec.ok ? '' : ' · FAILED'}`;
    if (this.resultPre) this.resultPre.textContent = rec.ok ? JSON.stringify(rec.result, null, 2) : `error: ${rec.error}`;
  }

  private renderLog(): void {
    const body = this.logBody;
    if (!body) return;
    body.textContent = '';
    for (const r of [...this.records].reverse()) {
      const tr = document.createElement('tr');
      tr.dataset.probeLog = r.id;
      tr.style.cssText = 'cursor:pointer;border-top:1px solid var(--ax-line)';
      tr.onclick = (): void => this.showResult(r);
      const cells = [new Date(r.at).toLocaleTimeString(), r.id + (r.seconds ? ` (${r.seconds} s)` : ''), r.tier, `${r.ms.toFixed(0)} ms`, r.ok ? 'ok' : `failed: ${r.error}`];
      cells.forEach((c, i) => { const td = document.createElement('td'); td.textContent = c; td.style.cssText = `padding:2px ${i === 0 ? '10px' : '6px'};${i === 3 ? 'text-align:right;' : ''}${r.ok ? '' : 'color:var(--ax-bad)'}`; tr.appendChild(td); });
      body.appendChild(tr);
    }
  }
}
